import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  SelectChangeEvent
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink } from 'react-router-dom';
// import PublicHeader from '../components/layout/PublicHeader'; // Handled by PublicPageLayout
// import PublicFooter from '../components/layout/PublicFooter'; // Handled by PublicPageLayout
import usePageTitle from '../hooks/usePageTitle';
import useCanonicalUrl from '../hooks/useCanonicalUrl';
// import LandingPageBackground from '../assets/images/LandingPageBackground.png'; // Replaced by PublicPageLayout
import PublicPageLayout from '../components/layout/PublicPageLayout'; // Import the new layout
import { getAllBlogPostsMetadata, formatBlogDate } from '../utils/blogUtils';

const BlogIndexPage: React.FC = () => {
  usePageTitle('WaveScoop Blog | Social Media Sentiment Analysis Insights');
  const canonicalUrl = useCanonicalUrl();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  
  // Get all blog posts from markdown files
  const allArticles = getAllBlogPostsMetadata();

  // Extract unique categories from articles
  const categories = useMemo(() => {
    const uniqueCategories = new Set(allArticles.map(article => article.category));
    return ['All', ...Array.from(uniqueCategories).sort()];
  }, [allArticles]);

  const filteredArticles = useMemo(() => {
    return allArticles.filter(article => {
      const matchesCategory = selectedCategory === 'All' || article.category === selectedCategory;
      const matchesSearch = 
        article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (article.tags && article.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())));
      return matchesCategory && matchesSearch;
    });
  }, [allArticles, searchTerm, selectedCategory]);

  const handleCategoryChange = (event: SelectChangeEvent<string>) => {
    setSelectedCategory(event.target.value as string);
  };

  return (
    <PublicPageLayout>
      <Helmet>
        <title>WaveScoop Blog | Social Media Sentiment Analysis Insights</title>
        <meta name="description" content="Explore WaveScoop's latest articles, guides, and insights on social media sentiment analysis, post simulation, AI in marketing, and more." />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      <Container component="main" maxWidth="lg" sx={{ py: { xs: 3, md: 5 } /* flexGrow: 1 is handled by PublicPageLayout's main Box */ }}>
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, sm: 4, md: 6 },
            bgcolor: 'rgba(15, 51, 89, 0.70)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            color: 'rgba(240, 240, 240, 0.95)',
            borderRadius: 2
          }}
        >
          <Typography variant="h1" component="h1" sx={{ textAlign: 'center', mb: 2, fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' }, fontWeight: 'bold' }}>
            WaveScoop Blog
          </Typography>
          <Typography variant="h5" component="p" sx={{ textAlign: 'center', color: 'rgba(220, 220, 220, 0.85)', mb: 5, fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
            Your source for the latest in social media sentiment, AI-powered marketing, and predictive analytics.
          </Typography>

          <Grid container spacing={3} sx={{ mb: 4 }} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                variant="outlined"
                label="Search Articles"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputLabelProps={{ sx: { color: 'rgba(220, 220, 220, 0.7)' } }}
                InputProps={{ sx: { color: 'rgba(240, 240, 240, 0.95)', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' } } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined" sx={{ 
                 '& .MuiInputLabel-root': { color: 'rgba(220, 220, 220, 0.7)' },
                 '& .MuiOutlinedInput-root': {
                    color: 'rgba(240, 240, 240, 0.95)',
                   '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                   '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                   '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'secondary.main' },
                 },
                 '& .MuiSelect-icon': { color: 'rgba(220, 220, 220, 0.7)'}
              }}>
                <InputLabel id="category-select-label">Category</InputLabel>
                <Select
                  labelId="category-select-label"
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  label="Category"
                >
                  {categories.map(category => (
                    <MenuItem key={category} value={category} sx={{ color: 'text.primary' }}>{category}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {filteredArticles.length > 0 ? (
            <Grid container spacing={4}>
              {filteredArticles.map((article) => (
                <Grid item xs={12} sm={6} md={4} key={article.slug}>
                  <Card sx={{
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    bgcolor: 'rgba(10, 40, 70, 0.5)',
                    color: 'rgba(230, 230, 230, 0.9)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 16px rgba(0,0,0,0.3)'
                    }
                  }}>
                    <CardActionArea component={RouterLink} to={`/blog/${article.slug}`} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch'}}>
                      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                        <Typography gutterBottom variant="h5" component="h2" sx={{ fontWeight: 'bold', color: 'rgba(250, 250, 250, 0.98)' }}>
                          {article.title}
                        </Typography>
                        <Typography variant="body2" color="rgba(210, 210, 210, 0.85)" sx={{ mb: 1, flexGrow: 1 }}>
                          {article.description}
                        </Typography>
                        <Box sx={{ mt: 'auto' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Chip label={article.category} size="small" sx={{ backgroundColor: 'secondary.dark', color: 'white' }} />
                            {article.readingTime && (
                              <Typography variant="caption" sx={{ color: 'rgba(200, 200, 200, 0.8)' }}>
                                {article.readingTime}
                              </Typography>
                            )}
                          </Box>
                          <Typography variant="caption" display="block" sx={{ color: 'rgba(180, 180, 180, 0.7)', mt:1 }}>
                            {formatBlogDate(article.date)}
                          </Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography sx={{ textAlign: 'center', mt: 4, color: 'rgba(220, 220, 220, 0.8)' }}>
              No articles found matching your criteria. Try adjusting your search or category.
            </Typography>
          )}

        </Paper>
      </Container>
    </PublicPageLayout>
  );
};

export default BlogIndexPage; 